import { Autocomplete } from '@mui/joy';
import { Stack, Typography, Button, Skeleton } from '@mui/material';
import clsx from 'clsx';
import React, { ChangeEvent, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import useSWR from 'swr';

import SelectLinkedReport from './selectLinkedReport';
import SelectStatus from './selectStatus';

import { ReportActionsDataProps } from '../../../../interfaces/reportActions';
import { setRemoveReporterFlag } from '../../../../slices/piiRemovalSlice';
import {
  setAvailableActions,
  setCurrencyChoices,
} from '../../../../slices/reportActionsSlice';
import { RootState } from '../../../../utils/store';
import {
  getFieldValue,
  setFieldValue,
  transformReportActionFieldsOrderFromAPI,
} from '../helper';

import { Checkbox } from '~/components/ui/checkbox';
import { DatePicker } from '~/components/ui/date-picker';
import { Input, inputFieldClasses } from '~/components/ui/input';
import { Option, Select } from '~/components/ui/select';
import { useTenantSlug } from '~/features/tenant/helpers';
import { useLocalizedCaseManagerFieldData } from '~/hooks/fetcher/useCaseManagerFieldData';
import useReportTypeData from '~/hooks/fetcher/useReportTypeData';
import { useAppSelector } from '~/hooks/global/typedReduxHooks';
import { ReportService } from '~/services/api/reports/endpoints';
import { useIntakeChannel } from '~/slices/intakeChannelSlice';

const subHeaderText = {
  color: 'black',
  fontFamily: '"Schibsted Grotesk", sans-serif, Arial',
  fontWeight: 600,
  fontSize: '1rem',
};

export interface MainActionData {
  reportActionData: ReportActionsDataProps;
  setReportActionData: React.Dispatch<
    React.SetStateAction<ReportActionsDataProps>
  >;
  disabledFlag: boolean;
}

const MainActions = ({
  reportActionData,
  setReportActionData,
  disabledFlag,
}: MainActionData) => {
  const dispatch = useDispatch();
  const slug = useTenantSlug();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const { piiRemovalFlag } = useSelector(
    (state: RootState) => state.piiRemoval,
  );
  const availableActions = useSelector(
    (state: RootState) => state.reportAction.availableActions,
  );

  const caseActionsQuery = ReportService.endpoints.getCaseActions.useQuery(
    {
      slug,
      channelId: selectedChannel ?? 0,
    },
    { skip: !slug || !selectedChannel },
  );
  const { getLocalizedCaseManagerFieldTitle } =
    useLocalizedCaseManagerFieldData();

  useEffect(() => {
    if (caseActionsQuery.isSuccess) {
      const displayedActions = caseActionsQuery.data.filter(
        (a) => a.order && a.is_display_on_report_actions_enabled,
      );
      dispatch(
        setAvailableActions(
          //eslint-disable-next-line
          //@ts-ignore
          transformReportActionFieldsOrderFromAPI(displayedActions),
        ),
      );
    }
  }, [caseActionsQuery, dispatch]);

  const deleteReporterClick = () => {
    dispatch(setRemoveReporterFlag(true));
  };

  const RenderCustomFields = (
    fieldTitle: string,
    fieldType: number,
    options: string[],
    fieldId: number,
  ) => {
    switch (fieldTitle) {
      case 'Due Date':
        return (
          <DatePicker
            disabled={disabledFlag}
            size='lg'
            controlled={false}
            selected={reportActionData.dueDate}
            onDayClick={(date) => {
              setReportActionData({
                ...reportActionData,
                dueDate: date,
              });
            }}
          />
        );
      case 'Next Review':
        return (
          <DatePicker
            disabled={disabledFlag}
            size='lg'
            placeholder=''
            controlled={false}
            selected={reportActionData.nextReview ?? undefined}
            onDayClick={(date) => {
              setReportActionData({
                ...reportActionData,
                nextReview: date,
              });
            }}
          />
        );
      case 'Status':
        return (
          <SelectStatus
            disabledFlag={disabledFlag}
            selected={reportActionData.status}
            onChangeSelected={(value) => {
              setReportActionData({
                ...reportActionData,
                status: value,
              });
            }}
          />
        );
      case 'Priority':
        return (
          <Select
            size='lg'
            disabled={disabledFlag}
            controlled={false}
            value={reportActionData.priority}
            onChange={(_, value) => {
              setReportActionData({
                ...reportActionData,
                // include 0 since this causes error since 0 is also considered false
                priority:
                  value || value === 0
                    ? Number(value)
                    : reportActionData.priority,
              });
            }}
            placeholder=''
            mountOptionsToRoot
          >
            <PriorityOptions />
          </Select>
        );
      case 'Report Type':
        return (
          <Select
            size='lg'
            disabled={disabledFlag}
            controlled={false}
            value={Number(reportActionData.reportType)}
            onChange={(_, value) => {
              setReportActionData({
                ...reportActionData,
                reportType: `${value ?? reportActionData.reportType}`,
              });
            }}
            mountOptionsToRoot
          >
            <ReportTypeOptions />
          </Select>
        );
      case 'Assigned Department':
        return (
          <Select
            size='lg'
            disabled={disabledFlag}
            controlled={false}
            value={Number(reportActionData.department)}
            onChange={(_, value) => {
              setReportActionData({
                ...reportActionData,
                department: `${value ?? reportActionData.department}`,
              });
            }}
            placeholder=''
            mountOptionsToRoot
          >
            <DepartmentOptions />
          </Select>
        );
      case 'Assignee':
        return (
          <Select
            size='lg'
            disabled={disabledFlag}
            controlled={false}
            value={Number(reportActionData.assignee)}
            onChange={(_, value) => {
              setReportActionData({
                ...reportActionData,
                assignee: `${value ?? reportActionData.assignee}`,
              });
            }}
            placeholder=''
            mountOptionsToRoot
          >
            <AssigneeOptions />
          </Select>
        );
      case 'Subject Individual':
        return (
          <Input
            size='lg'
            disabled={disabledFlag}
            value={reportActionData.subjectIndividual}
            onChange={(e) =>
              setReportActionData({
                ...reportActionData,
                subjectIndividual: e.target.value,
              })
            }
          />
        );
      case 'Suspected Department':
        return (
          <Input
            size='lg'
            disabled={disabledFlag}
            value={reportActionData.suspectedDepartment}
            onChange={(e) =>
              setReportActionData({
                ...reportActionData,
                suspectedDepartment: e.target.value,
              })
            }
          />
        );
      case 'Reporter':
        return <ReporterField value={reportActionData.reporter} />;
      case 'Linked Report':
        return (
          <SelectLinkedReport
            disabledFlag={disabledFlag}
            selected={reportActionData.linkedReport}
            onChangeSelected={(val) => {
              setReportActionData({ ...reportActionData, linkedReport: val });
            }}
          />
        );
      case 'Tags':
        return (
          <Autocomplete
            className={clsx(inputFieldClasses)}
            size='lg'
            disabled={disabledFlag}
            multiple
            id='tags-filled'
            options={[]}
            value={reportActionData.selectedTags}
            onChange={(_, values) => {
              setReportActionData({
                ...reportActionData,
                selectedTags: values,
              });
            }}
            freeSolo
          />
        );
      case 'Followers':
        return (
          <SelectFollower
            disabledFlag={disabledFlag}
            selectedUsers={reportActionData.followers}
            onChangeSelected={(followersList) => {
              setReportActionData({
                ...reportActionData,
                followers: followersList,
              });
            }}
          />
        );
      case 'Loss Value':
        return (
          <LossValueField
            disabledFlag={disabledFlag}
            selected={reportActionData.lossValue ?? ''}
            onChangeSelected={(e) => {
              setReportActionData({
                ...reportActionData,
                lossValue: e,
              });
            }}
            selectedCurrency={reportActionData.lossValueCurrency ?? ''}
            onChangeSelectedCurrency={(value) => {
              setReportActionData({
                ...reportActionData,
                lossValueCurrency: value,
              });
            }}
          />
        );
      case 'Report Outcome':
        return (
          <Select
            size='lg'
            disabled={disabledFlag}
            controlled={false}
            value={reportActionData.reportOutcome}
            onChange={(_, value) => {
              setReportActionData({
                ...reportActionData,
                reportOutcome: `${value ?? reportActionData.reportOutcome}`,
              });
            }}
            placeholder=''
            mountOptionsToRoot
          >
            {options.map((option) => (
              <Option key={option} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        );
      case 'Employee Sanction':
        return (
          <Select
            size='lg'
            disabled={disabledFlag}
            controlled={false}
            value={reportActionData.employeeSanction}
            onChange={(_, value) => {
              setReportActionData({
                ...reportActionData,
                employeeSanction: `${
                  value ?? reportActionData.employeeSanction
                }`,
              });
            }}
            placeholder=''
            mountOptionsToRoot
          >
            {options.map((option, key) => (
              <Option key={`${option}-${key}`} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        );
      default:
        if (fieldType == 0) {
          return (
            <Input
              size='lg'
              disabled={disabledFlag}
              value={
                getFieldValue(
                  reportActionData.reportActionFieldValues,
                  fieldTitle,
                  fieldId,
                ).value
              }
              onChange={(e) =>
                setReportActionData({
                  ...reportActionData,
                  reportActionFieldValues: setFieldValue(
                    reportActionData.reportActionFieldValues,
                    fieldTitle,
                    fieldId,
                    e.target.value,
                  ),
                })
              }
            />
          );
        }
        if (fieldType == 1) {
          return (
            <DatePicker
              disabled={disabledFlag}
              size='lg'
              controlled={false}
              placeholder=''
              selected={(() => {
                const value = getFieldValue(
                  reportActionData.reportActionFieldValues,
                  fieldTitle,
                  fieldId,
                )?.value;

                return value && !isNaN(Date.parse(value))
                  ? new Date(value)
                  : undefined;
              })()}
              onDayClick={(date) => {
                setReportActionData({
                  ...reportActionData,
                  reportActionFieldValues: setFieldValue(
                    reportActionData.reportActionFieldValues,
                    fieldTitle,
                    fieldId,
                    date.toDateString(),
                  ),
                });
              }}
            />
          );
        }
        if (fieldType == 2) {
          return (
            <Select
              size='lg'
              disabled={disabledFlag}
              controlled={false}
              value={
                getFieldValue(
                  reportActionData.reportActionFieldValues,
                  fieldTitle,
                  fieldId,
                ).value
              }
              onChange={(_, value) => {
                if (!value) return;
                setReportActionData({
                  ...reportActionData,
                  reportActionFieldValues: setFieldValue(
                    reportActionData.reportActionFieldValues,
                    fieldTitle,
                    fieldId,
                    `${value}`,
                  ),
                });
              }}
              placeholder=''
              mountOptionsToRoot
            >
              {options.map((option, key) => (
                <Option key={`${option}-${key}`} value={option}>
                  {option}
                </Option>
              ))}
            </Select>
          );
        }
        return '';
    }
  };

  const renderPiiRemovalButton = (title: string) => {
    if (
      piiRemovalFlag === true &&
      reportActionData.reporter !== '' &&
      title === 'Reporter'
    )
      return (
        <Button
          onClick={deleteReporterClick}
          sx={{ marginLeft: 'auto' }}
          disableTouchRipple
        >
          <Typography
            sx={{
              color: 'red',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              fontFamily: 'Schibsted Grotesk',
            }}
          >
            <FormattedMessage defaultMessage='Remove Reporter' />
          </Typography>
        </Button>
      );
    else return '';
  };

  return (
    <>
      {caseActionsQuery.isLoading && <Skeleton />}
      {availableActions.map((action, key) => (
        <Stack key={`${action.id}-${key}`}>
          <Stack flexDirection='row' key={`${action.id}-${key}`}>
            {' '}
            <Typography sx={subHeaderText}>
              {getLocalizedCaseManagerFieldTitle(action.title)}
            </Typography>
            {renderPiiRemovalButton(action.title)}
          </Stack>
          {RenderCustomFields(
            action.title,
            action.fieldType,
            action.choices,
            action.id,
          )}
        </Stack>
      ))}
    </>
  );
};

export default MainActions;

function PriorityOptions() {
  const intl = useIntl();
  const priorityOptions: Array<{
    id: number;
    name: string;
  }> = [
    {
      id: 0,
      name: intl.formatMessage({ defaultMessage: 'Low' }),
    },
    {
      id: 1,
      name: intl.formatMessage({ defaultMessage: 'Medium' }),
    },
    {
      id: 2,
      name: intl.formatMessage({ defaultMessage: 'High' }),
    },
    {
      id: 3,
      name: intl.formatMessage({ defaultMessage: 'Critical' }),
    },
  ];

  return (
    <>
      {priorityOptions.map((priority) => (
        <Option key={priority.id} value={priority.id}>
          {priority.name}
        </Option>
      ))}
    </>
  );
}

function ReportTypeOptions() {
  const { reportTypeList } = useReportTypeData();

  return (
    <>
      {reportTypeList.map((reportType) => (
        <Option key={reportType.id} value={reportType.id}>
          {reportType.name}
        </Option>
      ))}
    </>
  );
}

function DepartmentOptions() {
  const allDepartment = useAppSelector(
    (state) => state.reportData.departmentList,
  );

  return (
    <>
      {allDepartment.map((department) => (
        <Option key={department.id} value={department.id}>
          {department.name}
        </Option>
      ))}
    </>
  );
}

function AssigneeOptions() {
  const allUserTenant = useAppSelector(
    (state) => state.reportData.allUserTenant,
  );

  return (
    <>
      {allUserTenant.map((user) => (
        <Option key={user.id} value={user.id}>
          {user.full_name}
        </Option>
      ))}
    </>
  );
}

function ReporterField({ value }: Readonly<{ value: string }>) {
  const intl = useIntl();
  const reportData = useAppSelector((state) => state.reportData.report);
  const hasValue = value !== null && value !== '';
  const piiRemovalFlag = useAppSelector(
    (state) => state.piiRemoval.piiRemovalFlag,
  );
  const removeReporterFlag = useAppSelector(
    (state) => state.piiRemoval.removeReporterFlag,
  );

  return (
    <>
      <Input
        size='lg'
        className={clsx({
          '!font-bold !text-red-500': !hasValue,
          'line-through': piiRemovalFlag && removeReporterFlag,
        })}
        value={
          hasValue ? value : intl.formatMessage({ defaultMessage: 'Removed' })
        }
      />
      {reportData.isInternalSubmission && (
        <div>
          <Typography display='inline' fontSize={14} color='#878787'>
            <FormattedMessage defaultMessage='Marked as:' />{' '}
          </Typography>
          <Typography display='inline' fontSize={14}>
            <FormattedMessage defaultMessage='Internal Submission' />
          </Typography>
        </div>
      )}
    </>
  );
}

const LossValueField = ({
  selected,
  selectedCurrency,
  onChangeSelected,
  onChangeSelectedCurrency,
  disabledFlag,
}: {
  selected: string;
  selectedCurrency: string;
  onChangeSelectedCurrency: (e: string) => void;
  onChangeSelected: (e: string) => void;
  disabledFlag: boolean | undefined;
}) => {
  const dispatch = useDispatch();
  const { data } = useSWR('currency-choices', { revalidateOnFocus: false });
  const currencyChoices = useAppSelector(
    (state) => state.reportAction.currencyChoices,
  );

  useEffect(() => {
    if (data?.currency_choices)
      dispatch(setCurrencyChoices(data.currency_choices));
  }, [data, dispatch]);

  const onChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const regex = /^\d+(\.\d*)?$/; // regex to match currency-like value
    if (value === '' || regex.test(value)) {
      onChangeSelected(value);
    }
  };

  return (
    <Input
      size='lg'
      disabled={disabledFlag}
      startDecorator={
        <Select
          size='sm'
          value={selectedCurrency}
          onChange={(_, value) => {
            onChangeSelectedCurrency(`${value ?? selectedCurrency}`);
          }}
          mountOptionsToRoot
        >
          {currencyChoices?.map((item, key) => (
            <Option key={`${item}-${key}`} value={item}>
              {item}
            </Option>
          ))}
        </Select>
      }
      value={selected}
      onChange={onChangeValue}
    />
  );
};

function SelectFollower({
  selectedUsers,
  onChangeSelected,
  disabledFlag,
}: Readonly<{
  selectedUsers: Array<number>;
  onChangeSelected: (e: Array<number>) => void;
  disabledFlag: boolean | undefined;
}>) {
  const allUserTenant = useSelector(
    (state: RootState) => state.reportData.allUserTenant,
  );

  return (
    <Select
      size='lg'
      multiple
      disabled={disabledFlag}
      value={Array.isArray(selectedUsers) ? selectedUsers : []}
      onChange={(_, e) => {
        onChangeSelected(e as number[]);
      }}
      mountOptionsToRoot
      placeholder=''
    >
      {allUserTenant?.map(({ id, full_name }) => (
        <Option key={`${id}-${full_name}`} value={id}>
          <Checkbox checked={selectedUsers.indexOf(id) > -1} />
          {full_name}
        </Option>
      ))}
    </Select>
  );
}
